import EditorSection from "../EditorSection";
import { lookupProduct, ProductUnitPicker } from "./ProductUnitPicker";
import { defaultProductUnit } from "../../utils/ProductUnits";
import { Button, Dropdown, Input } from "@akin2unde/flowui-react";
import type { KeyValue } from "../../models/KeyValue";
import type { PurchaseItem } from "../../models/Purchase";
import type { InventoryCountItem } from "../../models/InventoryCount";
import { Status } from "../../models/Status";
import "./InventoryLines.css";
export function PurchaseLines({ items, products, onChange, }: {
    items: PurchaseItem[];
    products: KeyValue[];
    onChange: (x: PurchaseItem[]) => void;
}) {
    const update = (i: number, p: Partial<PurchaseItem>) => onChange(items.map((x, n) => (n === i ? { ...x, ...p } : x)));
    return (<div className="inventory-lines">
      <h3>Purchase items</h3>
      <p>Enter the quantity, amount and payment status for each product.</p>
      {items.map((x, i) => (<EditorSection key={i} className="admin-section line-section" title={<>Item {i + 1}</>}><div className="line-fields">
          
          <label>
            Product
            <Dropdown value={x.ProductCode} options={products.map((p) => ({
                display: String(p.Value),
                value: p.Key,
                other: null,
            }))} onChange={(p) => {
                const product = lookupProduct(products, String(p.value));
                const unit = defaultProductUnit(product);
                update(i, { ProductCode: String(p.value), UnitCode: unit?.UnitCode, BaseUnitQuantity: unit?.BaseQuantity ?? 0 });
            }}/>
          </label>
          <label>Unit<ProductUnitPicker product={lookupProduct(products, x.ProductCode)} value={x.UnitCode} factor={x.BaseUnitQuantity} onChange={unit => update(i, { UnitCode: unit.UnitCode, BaseUnitQuantity: unit.BaseQuantity })}/></label>
          <p>Stock received: {x.Quantity * (x.BaseUnitQuantity || 0)} base units</p>
          <label>
            Quantity
            <Input type="number" min={0} step={0.01} value={x.Quantity} onChange={(e) => update(i, { Quantity: Number(e.target.value) })}/>
          </label>
          <label>
            Amount
            <Input type="number" min={0} step={0.01} value={x.Amount} onChange={(e) => update(i, { Amount: Number(e.target.value) })}/>
          </label>
          <label>
            Payment
            <Dropdown value={x.PaymentStatus} options={[
                "Pending",
                "PartiallyPaid",
                "Paid",
                "Failed",
                "Refunded",
            ].map((v) => ({ display: v, value: v, other: null }))} onChange={(p) => update(i, {
                PaymentStatus: String(p.value) as PurchaseItem["PaymentStatus"],
            })}/>
          </label>
          <label>
            Status
            <Dropdown value={x.Status} options={Object.values(Status).map((v) => ({
                display: v,
                value: v,
                other: null,
            }))} onChange={(p) => update(i, { Status: String(p.value) as Status })}/>
          </label>
          <Button variant="outline" onClick={() => onChange(items.filter((_, n) => n !== i))}>
            Remove item {i + 1}
          </Button>
        </div></EditorSection>))}
      <Button variant="outline" onClick={() => onChange([
            ...items,
            {
                ProductCode: "",
                UnitCode: "",
                Quantity: 1,
                BaseUnitQuantity: 0,
                Amount: 0,
                Status: Status.Active,
                PaymentStatus: "Pending",
            },
        ])}>
        Add product
      </Button>
    </div>);
}
export function CountLines({ items, products, onChange, }: {
    items: InventoryCountItem[];
    products: KeyValue[];
    onChange: (x: InventoryCountItem[]) => void;
}) {
    const update = (i: number, p: Partial<InventoryCountItem>) => onChange(items.map((x, n) => (n === i ? { ...x, ...p } : x)));
    return (<div className="inventory-lines">
      <h3>Count items</h3>
      <p>All counts use the product base stock unit. Counts are saved for review. Saving does not adjust inventory.</p>
      {items.map((x, i) => (<EditorSection key={i} className="admin-section line-section" title={<>Item {i + 1}</>}><div className="line-fields">
          
          <label>
            Product
            <Dropdown value={x.ProductCode} options={products.map((p) => ({
                display: String(p.Value),
                value: p.Key,
                other: null,
            }))} onChange={(p) => update(i, { ProductCode: String(p.value) })}/>
          </label>
          <p>Base unit: {lookupProduct(products, x.ProductCode)?.BaseUnitCode || lookupProduct(products, x.ProductCode)?.UnitPrices?.find(u => u.BaseQuantity === 1)?.UnitCode || "Not configured"}</p>
          <label>
            System quantity (base units)
            <Input type="number" step={0.01} value={x.QuantityAvailable} disabled/>
          </label>
          <label>
            Counted quantity (base units)
            <Input type="number" min={0} step={0.01} value={x.QuantityCounted} onChange={(e) => update(i, { QuantityCounted: Number(e.target.value) })}/>
          </label>
          <label>
            Reason
            <Input value={x.Reason ?? ""} onChange={(e) => update(i, { Reason: e.target.value })}/>
          </label>
          <p>
            Variance: <strong>{x.QuantityCounted - x.QuantityAvailable}</strong>
          </p>
          <Button variant="outline" onClick={() => onChange(items.filter((_, n) => n !== i))}>
            Remove item {i + 1}
          </Button>
        </div></EditorSection>))}
      <Button variant="outline" onClick={() => onChange([
            ...items,
            {
                ProductCode: "",
                QuantityAvailable: 0,
                QuantityCounted: 0,
                Reason: "",
            },
        ])}>
        Add product
      </Button>
    </div>);
}
export function validateInventoryLines(items: unknown, purchase: boolean): string {
    if (!Array.isArray(items) || items.length === 0)
        return "Add at least one product.";
    const codes = new Set<string>();
    for (const item of items) {
        if (!item.ProductCode)
            return "Select a product on every line.";
        const key = purchase ? `${item.ProductCode}:${item.UnitCode || item.BaseUnitQuantity}` : item.ProductCode;
        if (codes.has(key))
            return "Each product can appear only once.";
        codes.add(key);
        if (purchase && (!Number.isFinite(item.BaseUnitQuantity) || item.BaseUnitQuantity <= 0))
            return "Select a product unit with a valid conversion on every line.";
        const qty = purchase ? item.Quantity : item.QuantityCounted;
        if (!Number.isFinite(qty) || qty < 0 || (purchase && qty === 0))
            return purchase
                ? "Quantities must be greater than zero."
                : "Counted quantities cannot be negative.";
        if (purchase && (!Number.isFinite(item.Amount) || item.Amount < 0))
            return "Amounts cannot be negative.";
        if (!purchase && !Number.isFinite(item.QuantityAvailable))
            return "System quantities must be finite numbers.";
    }
    return "";
}
